import store from '@/store/index';
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import i18n from '@/plugins/i18n';
import BIControlPanelService from '@/services/bi-control-panel-service';
import {
  BIActionResult,
  BIApprovalFilter,
  BIApprovalFilterField,
  BIApprovalFilterOptionField,
  BIApprovalTableRow,
  BIControlPanelModel,
  BIControlPanelTab,
  BIFilterFieldValueAction,
  BIFilterOptionFieldKey,
  BIFilterOptionPagination,
  BIFilterOptionsSearchAction,
  BIFilterOptionsSearchInputAction,
  BIFilterValue,
  BIPaginatedResponse,
  BIParametersFilter,
  BIParametersFilterField,
  BIParametersFilterOptionField,
  BIParameterTableRow,
  BISelectOption,
  BIStatusUpdateRequest,
  BIStatusUpdateResponse,
  BITablePagination,
} from './bi-control-panel-types';
import {
  defaultApprovalFilter,
  defaultApprovalFilterOptions,
  defaultBIControlPanelModel,
  defaultFilterOptionFieldState,
  defaultParametersFilter,
  defaultParametersFilterOptions,
} from './bi-control-panel-factory';

const biControlPanelService = new BIControlPanelService();
const translate = (key: string) => i18n.t(key).toString();

const PARAMETERS_ALWAYS_VISIBLE_FILTER_OPTION_FIELDS: BIParametersFilterOptionField[] = [
  'financialGroup',
  'contract',
  'subcontract',
];

const PARAMETERS_EXPANDED_FILTER_OPTION_FIELDS: BIParametersFilterOptionField[] = [
  'carrier',
  'competency',
];

const PARAMETERS_FILTER_FIELDS_TO_CLEAR: Partial<
  Record<BIParametersFilterField, BIParametersFilterField[]>
> = {
  financialGroup: ['contract', 'subcontract', 'carrier'],
  contract: ['subcontract'],
};

const APPROVAL_FILTER_FIELDS_TO_CLEAR: Partial<Record<BIApprovalFilterField, BIApprovalFilterField[]>> = { financialGroup: ['contract', 'carrier'] };

const PARAMETERS_FILTER_OPTION_FIELDS_TO_RESET: Partial<
  Record<BIParametersFilterField, BIParametersFilterOptionField[]>
> = {
  financialGroup: ['contract', 'subcontract', 'carrier', 'competency'],
  contract: ['subcontract', 'carrier', 'competency'],
  carrier: ['competency'],
};

const APPROVAL_FILTER_OPTION_FIELDS_TO_RESET: Partial<
  Record<BIApprovalFilterField, BIApprovalFilterOptionField[]>
> = {
  financialGroup: ['contract', 'carrier', 'competency'],
  contract: ['carrier', 'competency'],
  carrier: ['competency'],
};

type BITabFilterPatch = Partial<Record<string, BIFilterValue>>;
type BITabTableRows = BIParameterTableRow[] | BIApprovalTableRow[];
type BIFilterOptionsBatchField = BIParametersFilterOptionField | BIApprovalFilterOptionField;
type BIGenericTabState = {
  initialized: boolean;
  filter: Record<string, BIFilterValue>;
  filterOptions: Record<string, unknown>;
  table: {
    loading: boolean;
    rows: BITabTableRows;
    pagination: BITablePagination;
  };
};

@Module({
  namespaced: true,
  name: 'bi-control-panel',
  dynamic: true,
  store,
})
class BIControlPanelStore extends VuexModule {
  data: BIControlPanelModel = defaultBIControlPanelModel();

  get parametersFilter() {
    return this.data.parameters.filter;
  }

  get approvalFilter() {
    return this.data.approval.filter;
  }

  get parametersFilterOptions() {
    return this.data.parameters.filterOptions;
  }

  get approvalFilterOptions() {
    return this.data.approval.filterOptions;
  }

  get parametersTable() {
    return this.data.parameters.table;
  }

  get approvalTable() {
    return this.data.approval.table;
  }

  get parametersExpandedFiltersLoaded() {
    return this.data.parameters.expandedFiltersLoaded;
  }

  @Action
  async initializeParametersTab(): Promise<BIActionResult> {
    if (this.data.parameters.initialized) {
      return { success: true };
    }

    const tableResult = await this.searchParametersTable();

    if (!tableResult.success) {
      return tableResult;
    }

    this.SET_TAB_TABLE_HAS_MORE({ tab: 'parameters', hasMore: false });
    this.SET_TAB_INITIALIZED({ tab: 'parameters', initialized: true });

    return { success: true };
  }

  @Action
  async initializeApprovalTab(): Promise<BIActionResult> {
    if (this.data.approval.initialized) {
      return { success: true };
    }

    const tableResult = await this.searchApprovalTable();

    if (!tableResult.success) {
      return tableResult;
    }

    this.SET_TAB_INITIALIZED({ tab: 'approval', initialized: true });

    return { success: true };
  }

  @Action
  async loadParametersExpandedFilters(): Promise<BIActionResult> {
    if (this.data.parameters.expandedFiltersLoaded) {
      return { success: true };
    }

    const filterOptionRequests: Array<Promise<BIActionResult<BIPaginatedResponse<BISelectOption>>>> = [];

    if (this.data.parameters.filter.financialGroup) {
      filterOptionRequests.push(
        this.searchParametersFilterOptions({
          fieldKey: 'carrier',
          reset: true,
          searchText: '',
        }),
      );
    }

    const results = await Promise.all(filterOptionRequests);
    const firstError = results.find((result) => !result.success);

    if (firstError) {
      return firstError as BIActionResult;
    }

    this.SET_PARAMETERS_EXPANDED_FILTERS_LOADED(true);

    return { success: true };
  }

  @Action
  async clearParametersFilters(): Promise<BIActionResult> {
    this.RESET_TAB_FILTER('parameters');
    this.RESET_TAB_FILTER_OPTIONS('parameters');
    this.SET_TAB_TABLE_PAGE({ tab: 'parameters', page: 0 });
    this.SET_PARAMETERS_EXPANDED_FILTERS_LOADED(false);

    return this.searchParametersTable();
  }

  @Action
  async clearApprovalFilters(): Promise<BIActionResult> {
    this.RESET_TAB_FILTER('approval');
    this.RESET_TAB_FILTER_OPTIONS('approval');
    this.SET_TAB_TABLE_PAGE({ tab: 'approval', page: 0 });

    return this.searchApprovalTable();
  }

  @Action
  async submitParametersFilters(): Promise<BIActionResult> {
    this.SET_TAB_TABLE_PAGE({ tab: 'parameters', page: 0 });

    return this.searchParametersTable();
  }

  @Action
  async submitApprovalFilters(): Promise<BIActionResult> {
    this.SET_TAB_TABLE_PAGE({ tab: 'approval', page: 0 });

    return this.searchApprovalTable();
  }

  @Action
  async searchParametersTable(): Promise<BIActionResult> {
    return this.searchTableByTab('parameters', false, 99999);
  }

  @Action
  async searchApprovalTable(): Promise<BIActionResult> {
    return this.searchTableByTab('approval');
  }

  @Action
  async updateParametersStatus(payload: BIStatusUpdateRequest): Promise<BIActionResult> {
    return this.updateStatusByTab({
      tab: 'parameters',
      statusUpdateRequest: payload,
    });
  }

  @Action
  async updateApprovalStatus(payload: BIStatusUpdateRequest): Promise<BIActionResult> {
    return this.updateStatusByTab({
      tab: 'approval',
      statusUpdateRequest: payload,
    });
  }

  @Action
  async changeParametersTablePage(page: number): Promise<BIActionResult> {
    return this.changeTablePageByTab({
      tab: 'parameters',
      page,
    });
  }

  @Action
  async changeApprovalTablePage(page: number): Promise<BIActionResult> {
    return this.changeTablePageByTab({
      tab: 'approval',
      page,
    });
  }

  @Action
  async changeParametersTableSize(size: number): Promise<BIActionResult> {
    return this.changeTableSizeByTab({
      tab: 'parameters',
      size,
    });
  }

  @Action
  async changeApprovalTableSize(size: number): Promise<BIActionResult> {
    return this.changeTableSizeByTab({
      tab: 'approval',
      size,
    });
  }

  @Action
  async loadMoreParametersTable(): Promise<BIActionResult> {
    return this.loadMoreByTab('parameters');
  }

  @Action
  async loadMoreApprovalTable(): Promise<BIActionResult> {
    return this.loadMoreByTab('approval');
  }

  @Action
  setParametersFilterValue(payload: BIFilterFieldValueAction<BIParametersFilterField>) {
    const previousValue = this.data.parameters.filter[payload.fieldKey];
    const changed = previousValue !== payload.value;

    this.SET_TAB_FILTER({
      tab: 'parameters',
      filter: { [payload.fieldKey]: payload.value } as Partial<BIParametersFilter>,
    });

    if (!changed) {
      return;
    }

    this.APPLY_PARAMETERS_FILTER_DEPENDENCIES(payload.fieldKey);
  }

  @Action
  setApprovalFilterValue(payload: BIFilterFieldValueAction<BIApprovalFilterField>) {
    const previousValue = this.data.approval.filter[payload.fieldKey];
    const changed = previousValue !== payload.value;

    this.SET_TAB_FILTER({
      tab: 'approval',
      filter: { [payload.fieldKey]: payload.value } as Partial<BIApprovalFilter>,
    });

    if (!changed) {
      return;
    }

    this.APPLY_APPROVAL_FILTER_DEPENDENCIES(payload.fieldKey);
  }

  @Action
  setParametersFilterOptionSearchInput(
    payload: BIFilterOptionsSearchInputAction<BIParametersFilterOptionField>,
  ) {
    this.SET_TAB_FILTER_OPTIONS_SEARCH_INPUT({
      tab: 'parameters',
      fieldKey: payload.fieldKey,
      value: payload.value,
    });
  }

  @Action
  setApprovalFilterOptionSearchInput(
    payload: BIFilterOptionsSearchInputAction<BIApprovalFilterOptionField>,
  ) {
    this.SET_TAB_FILTER_OPTIONS_SEARCH_INPUT({
      tab: 'approval',
      fieldKey: payload.fieldKey,
      value: payload.value,
    });
  }

  @Action
  async searchParametersFilterOptions(
    payload: BIFilterOptionsSearchAction<BIParametersFilterOptionField>,
  ): Promise<BIActionResult<BIPaginatedResponse<BISelectOption>>> {
    return this.searchFilterOptionsByTab({
      tab: 'parameters',
      payload,
    });
  }

  @Action
  async searchApprovalFilterOptions(
    payload: BIFilterOptionsSearchAction<BIApprovalFilterOptionField>,
  ): Promise<BIActionResult<BIPaginatedResponse<BISelectOption>>> {
    return this.searchFilterOptionsByTab({
      tab: 'approval',
      payload,
    });
  }

  @Action
  private async loadParametersAlwaysVisibleFilterOptions(): Promise<BIActionResult> {
    return this.loadFilterOptionsBatch({
      tab: 'parameters',
      fields: PARAMETERS_ALWAYS_VISIBLE_FILTER_OPTION_FIELDS,
    });
  }

  @Mutation
  resetModule() {
    this.data = defaultBIControlPanelModel();
  }

  @Mutation
  private SET_TAB_INITIALIZED(payload: { tab: BIControlPanelTab; initialized: boolean }) {
    (this.data[payload.tab] as unknown as BIGenericTabState).initialized = payload.initialized;
  }

  @Mutation
  private SET_PARAMETERS_EXPANDED_FILTERS_LOADED(value: boolean) {
    this.data.parameters.expandedFiltersLoaded = value;
  }

  @Mutation
  private RESET_TAB_FILTER(tab: BIControlPanelTab) {
    (this.data[tab] as unknown as BIGenericTabState).filter = tab === 'parameters'
      ? defaultParametersFilter()
      : defaultApprovalFilter();
  }

  @Mutation
  private SET_TAB_FILTER(payload: { tab: BIControlPanelTab; filter: BITabFilterPatch }) {
    const tabState = this.data[payload.tab] as unknown as BIGenericTabState;

    const filteredEntries = Object.entries(payload.filter).filter(
      ([, value]) => value !== undefined,
    ) as Array<[string, BIFilterValue]>;

    tabState.filter = {
      ...tabState.filter,
      ...Object.fromEntries(filteredEntries),
    };
  }

  @Mutation
  private RESET_TAB_FILTER_OPTIONS(tab: BIControlPanelTab) {
    (this.data[tab] as unknown as BIGenericTabState).filterOptions = tab === 'parameters'
      ? defaultParametersFilterOptions()
      : defaultApprovalFilterOptions();
  }

  @Mutation
  private RESET_TAB_FILTER_OPTIONS_FIELD(payload: {
    tab: BIControlPanelTab;
    fieldKey: BIFilterOptionFieldKey;
  }) {
    const tabFilterOptions = (
      this.data[payload.tab] as unknown as BIGenericTabState
    ).filterOptions as Record<string, unknown>;

    if (!(payload.fieldKey in tabFilterOptions)) {
      return;
    }

    tabFilterOptions[payload.fieldKey] = defaultFilterOptionFieldState();
  }

  @Mutation
  private RESET_TAB_FILTER_OPTIONS_FIELDS(payload: {
    tab: BIControlPanelTab;
    fieldKeys: BIFilterOptionFieldKey[];
  }) {
    const tabFilterOptions = (
      this.data[payload.tab] as unknown as BIGenericTabState
    ).filterOptions as Record<string, unknown>;

    payload.fieldKeys.forEach((fieldKey) => {
      if (!(fieldKey in tabFilterOptions)) {
        return;
      }

      tabFilterOptions[fieldKey] = defaultFilterOptionFieldState();
    });
  }

  @Mutation
  private SET_TAB_FILTER_OPTIONS_SEARCH_INPUT(payload: {
    tab: BIControlPanelTab;
    fieldKey: BIFilterOptionFieldKey;
    value: string;
  }) {
    (
      (this.data[payload.tab] as unknown as BIGenericTabState).filterOptions as Record<
        string,
        { searchInput: string }
      >
    )[payload.fieldKey].searchInput = payload.value;
  }

  @Mutation
  private SET_TAB_FILTER_OPTIONS_LOADING(payload: {
    tab: BIControlPanelTab;
    fieldKey: BIFilterOptionFieldKey;
    loading: boolean;
  }) {
    (
      (this.data[payload.tab] as unknown as BIGenericTabState).filterOptions as Record<
        string,
        { loading: boolean }
      >
    )[payload.fieldKey].loading = payload.loading;
  }

  @Mutation
  private SET_TAB_FILTER_OPTIONS_INITIALIZED(payload: {
    tab: BIControlPanelTab;
    fieldKey: BIFilterOptionFieldKey;
    initialized: boolean;
  }) {
    (
      (this.data[payload.tab] as unknown as BIGenericTabState).filterOptions as Record<
        string,
        { initialized: boolean }
      >
    )[payload.fieldKey].initialized = payload.initialized;
  }

  @Mutation
  private SET_TAB_FILTER_OPTIONS_ITEMS(payload: {
    tab: BIControlPanelTab;
    fieldKey: BIFilterOptionFieldKey;
    items: BISelectOption[];
    reset: boolean;
  }) {
    const tabFilterOptions = (this.data[payload.tab] as unknown as BIGenericTabState).filterOptions as Record<
      string,
      { items: BISelectOption[] }
    >;
    const currentItems = tabFilterOptions[payload.fieldKey].items;

    tabFilterOptions[payload.fieldKey].items = payload.reset
      ? payload.items
      : [...currentItems, ...payload.items];
  }

  @Mutation
  private SET_TAB_FILTER_OPTIONS_PAGINATION(payload: {
    tab: BIControlPanelTab;
    fieldKey: BIFilterOptionFieldKey;
    pagination: BIFilterOptionPagination;
  }) {
    const tabFilterOptions = (this.data[payload.tab] as unknown as BIGenericTabState).filterOptions as Record<
      string,
      { pagination: BIFilterOptionPagination }
    >;

    tabFilterOptions[payload.fieldKey].pagination = {
      ...tabFilterOptions[payload.fieldKey].pagination,
      ...payload.pagination,
    };
  }

  @Mutation
  private SET_TAB_TABLE_LOADING(payload: { tab: BIControlPanelTab; loading: boolean }) {
    (this.data[payload.tab] as unknown as BIGenericTabState).table.loading = payload.loading;
  }

  @Mutation
  private SET_TAB_TABLE_ROWS(payload: { tab: BIControlPanelTab; rows: BITabTableRows }) {
    (this.data[payload.tab] as unknown as BIGenericTabState).table.rows = payload.rows;
  }

  @Mutation
  private SET_TAB_ROW_STATUS(payload: { tab: BIControlPanelTab; status: BIStatusUpdateResponse }) {
    const { table } = this.data[payload.tab] as unknown as BIGenericTabState;
    const rows = table.rows as Array<BIParameterTableRow | BIApprovalTableRow>;

    table.rows = rows.map((row) => (
      row.id === payload.status.id
        ? { ...row, approvalStatus: payload.status.approvalStatus }
        : row
    )) as BITabTableRows;
  }

  @Mutation
  private SET_TAB_TABLE_PAGINATION(payload: {
    tab: BIControlPanelTab;
    pagination: Partial<BITablePagination>;
  }) {
    const { table } = this.data[payload.tab] as unknown as BIGenericTabState;

    table.pagination = {
      ...table.pagination,
      ...payload.pagination,
    };
  }

  @Mutation
  private SET_TAB_TABLE_PAGE(payload: { tab: BIControlPanelTab; page: number }) {
    (this.data[payload.tab] as unknown as BIGenericTabState).table.pagination.page = payload.page;
  }

  @Mutation
  private SET_TAB_TABLE_SIZE(payload: { tab: BIControlPanelTab; size: number }) {
    (this.data[payload.tab] as unknown as BIGenericTabState).table.pagination.size = payload.size;
  }

  @Mutation
  private APPEND_TAB_TABLE_ROWS(payload: { tab: BIControlPanelTab; rows: BITabTableRows }) {
    const { table } = this.data[payload.tab] as unknown as BIGenericTabState;
    table.rows = [...table.rows, ...payload.rows] as BITabTableRows;
  }

  @Mutation
  private SET_TAB_TABLE_HAS_MORE(payload: { tab: BIControlPanelTab; hasMore: boolean }) {
    (this.data[payload.tab] as unknown as BIGenericTabState).table.pagination.hasMore = payload.hasMore;
  }

  @Action
  private async searchTableByTab(tab: BIControlPanelTab, append = false, sizeOverride?: number): Promise<BIActionResult> {
    const tabState = this.data[tab] as unknown as BIGenericTabState;
    const { size: paginationSize } = tabState.table.pagination;
    const size = sizeOverride ?? paginationSize;

    if (!tabState.filter.financialGroup) {
      this.SET_TAB_TABLE_ROWS({
        tab,
        rows: [] as unknown as BITabTableRows,
      });
      this.SET_TAB_TABLE_PAGINATION({
        tab,
        pagination: {
          page: 0,
          size,
          totalElements: 0,
          totalPages: 0,
        },
      });
      this.SET_TAB_TABLE_HAS_MORE({ tab, hasMore: false });

      return { success: true };
    }

    try {
      this.SET_TAB_TABLE_LOADING({ tab, loading: true });

      const { page } = tabState.table.pagination;
      const response = tab === 'parameters'
        ? await biControlPanelService.fetchParametersTable({
          page,
          size,
          filter: this.data.parameters.filter,
        })
        : await biControlPanelService.fetchApprovalTable({
          page,
          size,
          filter: this.data.approval.filter,
        });
      const result = response.data;

      if (append) {
        this.APPEND_TAB_TABLE_ROWS({
          tab,
          rows: result.content as BITabTableRows,
        });
      } else {
        this.SET_TAB_TABLE_ROWS({
          tab,
          rows: result.content as BITabTableRows,
        });
      }

      this.SET_TAB_TABLE_PAGINATION({
        tab,
        pagination: {
          page: result.page,
          size: result.size,
          totalElements: result.totalElements,
          totalPages: result.totalPages,
        },
      });

      this.SET_TAB_TABLE_HAS_MORE({
        tab,
        hasMore: !result.last,
      });

      return { success: true };
    } catch (error) {
      const axiosError = error as {
        message?: string;
        response?: {
          data?: {
            message?: string;
            error?: string;
          } | string;
        };
      };
      const responseData = axiosError?.response?.data;
      let errorMessage = translate('biControlPanel.messages.tableDataSearchError');

      if (typeof responseData === 'string' && responseData.trim()) {
        errorMessage = responseData;
      } else if (typeof responseData === 'object' && responseData) {
        if (typeof responseData.message === 'string' && responseData.message.trim()) {
          errorMessage = responseData.message;
        } else if (typeof responseData.error === 'string' && responseData.error.trim()) {
          errorMessage = responseData.error;
        }
      } else if (typeof axiosError?.message === 'string' && axiosError.message.trim()) {
        errorMessage = axiosError.message;
      }

      return {
        success: false,
        errorMessage,
      };
    } finally {
      this.SET_TAB_TABLE_LOADING({ tab, loading: false });
    }
  }

  @Action
  private async updateStatusByTab(payload: {
    tab: BIControlPanelTab;
    statusUpdateRequest: BIStatusUpdateRequest;
  }): Promise<BIActionResult> {
    const { tab, statusUpdateRequest } = payload;

    try {
      const response = tab === 'parameters'
        ? await biControlPanelService.updateParameterStatus(statusUpdateRequest)
        : await biControlPanelService.updateApprovalStatus(statusUpdateRequest);

      this.SET_TAB_ROW_STATUS({
        tab,
        status: response.data,
      });

      return { success: true };
    } catch (error) {
      const axiosError = error as {
        message?: string;
        response?: {
          data?: {
            message?: string;
            error?: string;
          } | string;
        };
      };
      const responseData = axiosError?.response?.data;
      let errorMessage = translate('biControlPanel.messages.statusUpdateError');

      if (typeof responseData === 'string' && responseData.trim()) {
        errorMessage = responseData;
      } else if (typeof responseData === 'object' && responseData) {
        if (typeof responseData.message === 'string' && responseData.message.trim()) {
          errorMessage = responseData.message;
        } else if (typeof responseData.error === 'string' && responseData.error.trim()) {
          errorMessage = responseData.error;
        }
      } else if (typeof axiosError?.message === 'string' && axiosError.message.trim()) {
        errorMessage = axiosError.message;
      }

      return {
        success: false,
        errorMessage,
      };
    }
  }

  @Action
  private async changeTablePageByTab(payload: {
    tab: BIControlPanelTab;
    page: number;
  }): Promise<BIActionResult> {
    const { tab, page } = payload;
    const tabState = this.data[tab] as unknown as BIGenericTabState;
    const currentPage = tabState.table.pagination.page;
    const { totalPages } = tabState.table.pagination;
    const normalizedPage = Math.max(0, page);
    const pageOutOfBounds = totalPages > 0 && normalizedPage >= totalPages;

    if (currentPage === normalizedPage || pageOutOfBounds) {
      return { success: true };
    }

    this.SET_TAB_TABLE_PAGE({ tab, page: normalizedPage });

    return this.searchTableByTab(tab);
  }

  @Action
  private async changeTableSizeByTab(payload: {
    tab: BIControlPanelTab;
    size: number;
  }): Promise<BIActionResult> {
    const { tab, size } = payload;
    const tabState = this.data[tab] as unknown as BIGenericTabState;
    const normalizedSize = Number(size);

    if (!normalizedSize || normalizedSize === tabState.table.pagination.size) {
      return { success: true };
    }

    this.SET_TAB_TABLE_SIZE({ tab, size: normalizedSize });
    this.SET_TAB_TABLE_PAGE({ tab, page: 0 });

    return this.searchTableByTab(tab);
  }

  @Action
  private async loadMoreByTab(tab: BIControlPanelTab): Promise<BIActionResult> {
    const tabState = this.data[tab] as unknown as BIGenericTabState;

    if (!tabState.table.pagination.hasMore || tabState.table.loading) {
      return { success: true };
    }

    const nextPage = tabState.table.pagination.page + 1;
    this.SET_TAB_TABLE_PAGE({ tab, page: nextPage });

    return this.searchTableByTab(tab, true);
  }

  @Action
  private async searchFilterOptionsByTab(actionPayload: {
    tab: BIControlPanelTab;
    payload: BIFilterOptionsSearchAction<BIFilterOptionFieldKey>;
  }): Promise<BIActionResult<BIPaginatedResponse<BISelectOption>>> {
    const { tab, payload } = actionPayload;
    const { fieldKey, reset = true } = payload;
    const tabState = this.data[tab] as unknown as BIGenericTabState;
    const tabFilterOptions = tabState.filterOptions as Record<
      string,
      {
        loading: boolean;
        items: BISelectOption[];
        searchInput: string;
        pagination: BIFilterOptionPagination;
      }
    >;
    const fieldState = tabFilterOptions[fieldKey];

    if (!fieldState) {
      return {
        success: true,
        data: this.emptyFilterOptionsResponse(),
      };
    }

    if (fieldState.loading && typeof payload.page !== 'number') {
      return {
        success: true,
        data: this.fieldStateToFilterOptionsResponse(fieldState),
      };
    }

    if (!reset && !fieldState.pagination.hasNext) {
      return {
        success: true,
        data: this.fieldStateToFilterOptionsResponse(fieldState),
      };
    }

    const searchText = typeof payload.searchText === 'string'
      ? payload.searchText
      : fieldState.searchInput;
    const page = typeof payload.page === 'number'
      ? payload.page
      : reset ? 0 : fieldState.pagination.page + 1;
    const fieldDefaultSize = fieldKey === 'contract' ? 10 : fieldState.pagination.size;
    const size = typeof payload.size === 'number'
      ? payload.size
      : fieldDefaultSize;

    this.SET_TAB_FILTER_OPTIONS_LOADING({ tab, fieldKey, loading: true });
    this.SET_TAB_FILTER_OPTIONS_SEARCH_INPUT({ tab, fieldKey, value: searchText });

    try {
      let response;

      switch (fieldKey) {
        case 'financialGroup':
          response = await biControlPanelService.fetchFinancialGroups(tab, {
            page,
            size,
            searchText,
            filter: tabState.filter,
          });
          break;
        case 'contract':
          response = await biControlPanelService.fetchContracts(tab, {
            page,
            size,
            searchText,
            filter: tabState.filter,
          });
          break;
        case 'subcontract':
          response = await biControlPanelService.fetchSubcontracts(tab, {
            page,
            size,
            searchText,
            filter: tabState.filter,
          });
          break;
        case 'carrier':
          response = await biControlPanelService.fetchCarriers(tab, {
            page,
            size,
            searchText,
            filter: tabState.filter,
          });
          break;
        case 'competency':
          response = await biControlPanelService.fetchCompetencies(tab, {
            page,
            size,
            searchText,
            filter: tabState.filter,
          });
          break;
        default:
          throw new Error(`Unsupported BI filter option field: ${fieldKey}`);
      }

      const result = response.data;

      this.SET_TAB_FILTER_OPTIONS_ITEMS({
        tab,
        fieldKey,
        items: result.content,
        reset,
      });
      this.SET_TAB_FILTER_OPTIONS_PAGINATION({
        tab,
        fieldKey,
        pagination: {
          page: result.page,
          size: result.size,
          totalElements: result.totalElements,
          hasNext: !result.last,
        },
      });
      this.SET_TAB_FILTER_OPTIONS_INITIALIZED({
        tab,
        fieldKey,
        initialized: true,
      });

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      const axiosError = error as {
        message?: string;
        response?: {
          data?: {
            message?: string;
            error?: string;
          } | string;
        };
      };
      const responseData = axiosError?.response?.data;
      let errorMessage = translate('biControlPanel.messages.filterOptionsLoadError');

      if (typeof responseData === 'string' && responseData.trim()) {
        errorMessage = responseData;
      } else if (typeof responseData === 'object' && responseData) {
        if (typeof responseData.message === 'string' && responseData.message.trim()) {
          errorMessage = responseData.message;
        } else if (typeof responseData.error === 'string' && responseData.error.trim()) {
          errorMessage = responseData.error;
        }
      } else if (typeof axiosError?.message === 'string' && axiosError.message.trim()) {
        errorMessage = axiosError.message;
      }

      return {
        success: false,
        errorMessage,
      };
    } finally {
      this.SET_TAB_FILTER_OPTIONS_LOADING({ tab, fieldKey, loading: false });
    }
  }

  @Action
  private async loadFilterOptionsBatch(payload: {
    tab: BIControlPanelTab;
    fields: BIFilterOptionsBatchField[];
  }): Promise<BIActionResult> {
    const { tab, fields } = payload;
    const results = await Promise.all(
      fields.map((fieldKey) => this.searchFilterOptionsByTab({
        tab,
        payload: {
          fieldKey,
          reset: true,
          searchText: '',
        },
      })),
    );

    const firstError = results.find((result) => !result.success);

    if (firstError) {
      return firstError as BIActionResult;
    }

    return { success: true };
  }

  private emptyFilterOptionsResponse(): BIPaginatedResponse<BISelectOption> {
    return {
      content: [],
      page: 0,
      size: 0,
      totalElements: 0,
      totalPages: 0,
      last: true,
    };
  }

  private fieldStateToFilterOptionsResponse(fieldState: {
    items: BISelectOption[];
    pagination: BIFilterOptionPagination;
  }): BIPaginatedResponse<BISelectOption> {
    return {
      content: fieldState.items,
      page: Math.max(fieldState.pagination.page, 0),
      size: fieldState.pagination.size,
      totalElements: fieldState.pagination.totalElements,
      totalPages: 0,
      last: !fieldState.pagination.hasNext,
    };
  }

  @Mutation
  private APPLY_PARAMETERS_FILTER_DEPENDENCIES(fieldKey: BIParametersFilterField) {
    const filterFieldsToClear = PARAMETERS_FILTER_FIELDS_TO_CLEAR[fieldKey] || [];
    const filterOptionFieldsToReset = PARAMETERS_FILTER_OPTION_FIELDS_TO_RESET[fieldKey] || [];

    if (filterFieldsToClear.length) {
      this.SET_TAB_FILTER({
        tab: 'parameters',
        filter: filterFieldsToClear.reduce((acc, field) => {
          acc[field] = null;

          return acc;
        }, {} as Record<BIParametersFilterField, null>),
      });
    }

    if (filterOptionFieldsToReset.length) {
      this.RESET_TAB_FILTER_OPTIONS_FIELDS({
        tab: 'parameters',
        fieldKeys: filterOptionFieldsToReset,
      });
    }

    if (filterOptionFieldsToReset.some((field) => PARAMETERS_EXPANDED_FILTER_OPTION_FIELDS.includes(field))) {
      this.SET_PARAMETERS_EXPANDED_FILTERS_LOADED(false);
    }
  }

  @Mutation
  private APPLY_APPROVAL_FILTER_DEPENDENCIES(fieldKey: BIApprovalFilterField) {
    const filterFieldsToClear = APPROVAL_FILTER_FIELDS_TO_CLEAR[fieldKey] || [];
    const filterOptionFieldsToReset = APPROVAL_FILTER_OPTION_FIELDS_TO_RESET[fieldKey] || [];

    if (filterFieldsToClear.length) {
      this.SET_TAB_FILTER({
        tab: 'approval',
        filter: filterFieldsToClear.reduce((acc, field) => {
          acc[field] = null;

          return acc;
        }, {} as Record<BIApprovalFilterField, null>),
      });
    }

    if (filterOptionFieldsToReset.length) {
      this.RESET_TAB_FILTER_OPTIONS_FIELDS({
        tab: 'approval',
        fieldKeys: filterOptionFieldsToReset,
      });
    }
  }
}

export default getModule(BIControlPanelStore);
